import 'ol/ol.css';
import Draw from 'ol/interaction/Draw';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { getArea, getLength } from 'ol/sphere';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Coordinate } from 'ol/coordinate';
import { unByKey } from 'ol/Observable';
import { useMap } from '@/state/context/MapContext';
import { HelpTooltip, HelpTooltipText } from './HelpTooltip';
import { MeasureTooltip } from './MeasureTooltip';
import useMapLayer from '../useMapLayer';
import { MeasurementType } from '@/state/MeasurementState';
import { useSelector } from 'react-redux';
import { RootState } from '@/state';
import { LineString, Polygon } from 'ol/geom';
import { unkinkPolygon } from '@turf/turf';
import { Polygon as OlPolygon } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';

export function MeasurementLayer({ zIndex }: { zIndex: number }) {
    const { map } = useMap();
    const projection = map.getView().getProjection();

    const [measures, setMeasures] = useState([]);
    const [helpTooltip, setHelpTooltip] = useState<HelpTooltipText>(HelpTooltipText.START);

    const measurementType = useSelector(
        ({ measurementState }: RootState) => measurementState.measurementType
    ) as MeasurementType;

    const formatArea = (polygon: Polygon): string => {
        // unkinkPolygon does not work with non-unique coordinates (except for the first/last one)
        // See: https://github.com/Turfjs/turf/issues/1679#issuecomment-492842419
        const uniqueCoords = new Set<string>();
        const coordinates = polygon.getCoordinates()[0].map((coord, index) => {
            let key = `${coord[0]}-${coord[1]}`;
            while (uniqueCoords.has(key)) {
                coord[0] += 1e-9;
                coord[1] += 1e-9;
                key = `${coord[0]}-${coord[1]}`;
            }
            uniqueCoords.add(key);
            return coord;
        });

        if (coordinates.length <= 3) return '0 m²';

        const filteredPolygon = new OlPolygon([coordinates]);
        const geoJSONFormat = new GeoJSON();
        const geoJsonPolygon = geoJSONFormat.writeGeometryObject(filteredPolygon, {
            featureProjection: projection,
        });
        const unkinked = unkinkPolygon(geoJsonPolygon);

        const area = unkinked.features.reduce((acc, feature) => {
            const olPolygon = geoJSONFormat.readGeometry(feature.geometry, { featureProjection: projection });
            return acc + getArea(olPolygon, { projection });
        }, 0);

        const formattedArea = area > 1e5 ? `${(area / 1e6).toFixed(2)} km²` : `${area.toFixed(2)} m²`;

        return `${formattedArea} (${formatLength(polygon)})`;
    };

    const formatLength = function (line: LineString | Polygon): string {
        const length = getLength(line, { projection });
        return length > 999 ? `${(length / 1000).toFixed(2)} km` : `${length.toFixed(2)} m`;
    };
    const onDrawStart = useCallback(
        evt => {
            const target = evt.feature;
            setHelpTooltip(HelpTooltipText.CONTINUE);
            const tooltipCoord: Coordinate = evt?.feature?.getGeometry()?.getLastCoordinate();
            const latest = { coordinates: tooltipCoord, value: 0, locked: false };
            setMeasures([...measures, latest]);
            target.getGeometry().on('change', e => {
                const geom = e.target;
                const value = geom instanceof Polygon ? formatArea(geom) : formatLength(geom);
                const coordinates = geom.getLastCoordinate();
                setMeasures([...measures, { ...latest, coordinates, value }]);
            });
        },
        [setHelpTooltip, setMeasures, formatArea, formatLength]
    );

    const onDrawEnd = useCallback(() => {
        setHelpTooltip(HelpTooltipText.START);
        setMeasures(prevMeasures => {
            const latest = prevMeasures[prevMeasures.length - 1];
            return [...prevMeasures.slice(0, -1), { ...latest, locked: true }];
        });
    }, [setHelpTooltip, setMeasures]);

    const layer = useMemo(
        () =>
            new VectorLayer({
                source: new VectorSource(),
                style: new Style({
                    fill: new Fill({
                        color: 'rgba(128, 128, 128, 0.4)',
                    }),
                    stroke: new Stroke({
                        color: '#00506E',
                        width: 3,
                    }),
                    image: new CircleStyle({
                        radius: 7,
                        fill: new Fill({
                            color: '#00506E',
                        }),
                    }),
                }),
            }),
        []
    );

    const draw = useMemo(() => {
        const style = new Style({
            fill: new Fill({
                color: 'rgba(255, 255, 255, 0.2)',
            }),
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 0.5)',
                lineDash: [10, 10],
                width: 2,
            }),
        });

        const drawObject = new Draw({
            source: layer.getSource(),
            type: measurementType,
            condition: e => e?.originalEvent?.button === 0,
            style: style,
        });

        return drawObject;
    }, [layer, measurementType]);

    useMapLayer({ layer, index: zIndex });
    useEffect(() => {
        map.addInteraction(draw);
        return () => {
            map.removeInteraction(draw);
        };
    }, [map, draw]);

    useEffect(() => {
        const startKey = draw.on('drawstart', onDrawStart);
        const endKey = draw.on('drawend', onDrawEnd);
        return () => {
            unByKey(startKey);
            unByKey(endKey);
        };
    }, [draw, onDrawStart, onDrawEnd]);

    return (
        <div id={'measurement-layer'}>
            <HelpTooltip tip={helpTooltip} />
            {measures.map(({ coordinates, value, locked }, i) => (
                <MeasureTooltip key={i} coordinates={coordinates} value={value} locked={locked} />
            ))}
        </div>
    );
}
